import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { CONTACT, LOGOS } from "@/lib/site";
import { STATIC_EXPORT } from "@/lib/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODEL_OPTIONS = ["Employer of Record (EOR)", "Offshore Development Centre (ODC)", "Centre of Excellence (CoE)", "Global Capability Centre (GCC)", "Not sure yet"];

const inputCls =
  "w-full rounded-2xl border border-[#0B132B]/12 bg-[#F6F3FE] px-4 py-3.5 text-sm text-[#0B132B] placeholder:text-[#9AA1B2] outline-none transition-colors duration-300 focus:border-[#5B4199] focus:bg-white focus:ring-2 focus:ring-[#5B4199]/15";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", model: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    if (STATIC_EXPORT) {
      // Static hosting has no backend - hand the enquiry to the visitor's email app, pre-filled.
      const subject = encodeURIComponent(`Website enquiry - ${form.name}${form.company ? ` (${form.company})` : ""}`);
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone || "-"}\nCompany: ${form.company || "-"}\nInterested in: ${form.model || "-"}\n\n${form.message}`
      );
      window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
      toast.success("Opening your email app - your enquiry is pre-filled and ready to send.");
      setSending(false);
      return;
    }
    try {
      await axios.post(`${API}/enquiry`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        company: form.company || null,
        model: form.model || null,
        message: form.message,
      });
      toast.success("Enquiry sent - we'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", model: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative -mt-8 rounded-t-[48px] py-20 sm:py-28 lg:py-32 bg-[#5B4199] overflow-hidden" data-testid="contact-section">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(800px 500px at 88% 10%, rgba(255,255,255,0.14), transparent 65%), radial-gradient(600px 460px at 5% 100%, rgba(11,19,43,0.25), transparent 70%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center rounded-full border border-white/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white mb-6">
              Engagement
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.04]">
              Put your India plans on a clear footing.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-md">
              Share your objectives and current stage. We'll discuss the operating model, scope, and
              commercial considerations relevant to your business.
            </p>

            <div className="mt-12 space-y-5" data-testid="contact-directory">
              <a
                href={CONTACT.phoneHref}
                className="group flex items-center gap-4 text-white/90 hover:text-white transition-colors"
                data-testid="contact-phone-link"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/12 group-hover:bg-white/20 transition-colors">
                  <Phone className="h-[18px] w-[18px] text-white" />
                </span>
                <span className="text-base font-semibold">{CONTACT.phoneDisplay}</span>
              </a>
              <a
                href={CONTACT.emailHref}
                className="group flex items-center gap-4 text-white/90 hover:text-white transition-colors"
                data-testid="contact-email-link"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/12 group-hover:bg-white/20 transition-colors">
                  <Mail className="h-[18px] w-[18px] text-white" />
                </span>
                <span className="text-base font-semibold">{CONTACT.email}</span>
              </a>
              <div className="flex items-start gap-4 text-white/90" data-testid="contact-address">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/12 shrink-0">
                  <MapPin className="h-[18px] w-[18px] text-white" />
                </span>
                <address className="not-italic text-base leading-relaxed">
                  {CONTACT.addressLines.map((l) => (
                    <span key={l} className="block">{l}</span>
                  ))}
                </address>
              </div>
            </div>

            <div className="mt-12 inline-flex items-center gap-3 rounded-full border border-white/25 bg-white/10 px-5 py-3">
              <img src={LOGOS.lionforce} alt="Lionforce" className="h-7 w-7 object-contain" />
              <p className="text-sm text-white/85">
                Expante is operated by <span className="font-semibold text-white">Lionforce Technologies Pvt Ltd.</span>
              </p>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-[32px] bg-white p-7 sm:p-9 space-y-5 shadow-[0_40px_90px_-30px_rgba(11,19,43,0.5)]"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cf-name" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7488] mb-2">Full name *</label>
                <input id="cf-name" required value={form.name} onChange={set("name")} placeholder="Jane Cooper" className={inputCls} data-testid="contact-input-name" />
              </div>
              <div>
                <label htmlFor="cf-email" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7488] mb-2">Work email *</label>
                <input id="cf-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@company.com" className={inputCls} data-testid="contact-input-email" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cf-phone" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7488] mb-2">Phone</label>
                <input id="cf-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+1 555 000 1234" className={inputCls} data-testid="contact-input-phone" />
              </div>
              <div>
                <label htmlFor="cf-company" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7488] mb-2">Company</label>
                <input id="cf-company" value={form.company} onChange={set("company")} placeholder="Acme Inc." className={inputCls} data-testid="contact-input-company" />
              </div>
            </div>
            <div>
              <label htmlFor="cf-model" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7488] mb-2">Engagement model</label>
              <select id="cf-model" value={form.model} onChange={set("model")} className={`${inputCls} appearance-none`} data-testid="contact-select-model">
                <option value="">Select a model…</option>
                {MODEL_OPTIONS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="cf-message" className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7488] mb-2">Message *</label>
              <textarea id="cf-message" required rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about the team you're planning…" className={`${inputCls} resize-none`} data-testid="contact-input-message" />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="group w-full inline-flex items-center justify-center gap-2 bg-[#5B4199] hover:bg-[#0B132B] disabled:opacity-60 text-white font-semibold rounded-full py-4 transition-colors duration-300"
              data-testid="contact-submit-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Speak with Expante
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
            <p className="text-xs text-[#6B7488] text-center">
              We respond within 24 hours. Your details stay with Expante / Lionforce.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
